import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { queryKeys } from '@/api/queryKeys';
import { get, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { timeAgo } from '@/lib/format';
import { Bell, Check, Trash2, BellOff, MessageSquare, Heart, UserPlus, Megaphone, Gift } from 'lucide-react';
import { cn } from '@/lib/cn';
import { EmptyState } from '@/components/atoms/EmptyState';

interface Notif {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link_url: string | null;
  is_read: boolean;
  created_at: string;
}

/* 타입별 아이콘/색상 — type 문자열은 백엔드 종류에 맞춰 접두 매칭 */
function iconFor(type: string) {
  const t = type.toLowerCase();
  if (t.includes('comment') || t.includes('reply') || t.includes('message'))
    return { Icon: MessageSquare, tone: 'text-indigo-500', bg: 'bg-indigo-50' };
  if (t.includes('like') || t.includes('vote'))
    return { Icon: Heart, tone: 'text-rose-500', bg: 'bg-rose-50' };
  if (t.includes('follow'))
    return { Icon: UserPlus, tone: 'text-emerald-500', bg: 'bg-emerald-50' };
  if (t.includes('notice') || t.includes('event'))
    return { Icon: Megaphone, tone: 'text-amber-500', bg: 'bg-amber-50' };
  if (t.includes('point') || t.includes('reward') || t.includes('exchange'))
    return { Icon: Gift, tone: 'text-violet-500', bg: 'bg-violet-50' };
  return { Icon: Bell, tone: 'text-[var(--color-primary)]', bg: 'bg-[var(--color-primary)]/10' };
}

export function NotificationsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notifications.list,
    queryFn: () => get<{ list: Notif[] }>('/notifications'),
  });

  const readAllMutation = useMutation({
    mutationFn: () => put('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list }),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => put(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => del(`/notifications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list }),
  });

  const list = data?.list ?? [];
  const unreadCount = list.filter((n) => !n.is_read).length;

  return (
    <div className="container-base py-10">
      <header className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-3xl font-extrabold">알림</h1>
            <p className="mt-2 text-sm text-[var(--color-text-sub)]">
              {unreadCount > 0 ? (
                <>
                  읽지 않은 알림이 <strong className="text-[var(--color-primary)]">{unreadCount}건</strong> 있어요.
                </>
              ) : (
                '새로운 알림이 오면 여기에서 확인할 수 있어요.'
              )}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => readAllMutation.mutate()}
              loading={readAllMutation.isPending}
            >
              <Check className="h-4 w-4" />
              모두 읽음
            </Button>
          )}
        </header>

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        )}

        {!isLoading && list.length === 0 && (
          <EmptyState
            icon={BellOff}
            title="아직 알림이 없어요"
            description="활동을 시작하면 알림이 이곳에 쌓여요."
          />
        )}

        {!isLoading && list.length > 0 && (
          <ul className="space-y-2">
            {list.map((n) => {
              const { Icon, tone, bg } = iconFor(n.type);
              const Inner = (
                <div
                  className={cn(
                    'flex items-start gap-3 rounded-2xl border p-4 transition-colors',
                    n.is_read
                      ? 'border-[var(--color-border)] bg-white hover:border-[var(--color-primary)]/40'
                      : 'border-[var(--color-primary)]/30 bg-[var(--color-primary)]/[0.04] hover:border-[var(--color-primary)]/60',
                  )}
                >
                  <div
                    className={cn(
                      'relative mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
                      n.is_read ? 'bg-[var(--color-bg)]' : bg,
                    )}
                  >
                    <Icon
                      className={cn('h-5 w-5', n.is_read ? 'text-[var(--color-text-sub)]' : tone)}
                    />
                    {!n.is_read && (
                      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[var(--color-primary)]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div
                        className={cn(
                          'min-w-0 flex-1 truncate text-sm font-semibold',
                          !n.is_read && 'text-[var(--color-primary)]',
                        )}
                      >
                        {n.title}
                      </div>
                      <span className="flex-shrink-0 text-xs text-[var(--color-text-sub)]">
                        {timeAgo(n.created_at)}
                      </span>
                    </div>
                    {n.body && (
                      <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-sub)]">
                        {n.body}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteMutation.mutate(n.id);
                    }}
                    className="flex-shrink-0 rounded-lg p-1.5 text-[var(--color-text-sub)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-fall)]"
                    aria-label="알림 삭제"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );

              return (
                <li
                  key={n.id}
                  onClick={() => !n.is_read && markReadMutation.mutate(n.id)}
                >
                  {n.link_url ? (
                    <Link to={n.link_url} className="block">
                      {Inner}
                    </Link>
                  ) : (
                    <div>{Inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
